import {
  installGuideText,
  notificationGuideText,
  type Environment,
  type InstallSetup,
  type NotificationBlocker,
  type NotificationSetup,
} from "@safers/pwa";

export type SetupTone = "on" | "off" | "blocked";

export interface SetupAction {
  label: string;
  onClick: () => void;
}

export type NotificationKey =
  | "on"
  | "ready"
  | "denied"
  | "insecure"
  | "no-push-service"
  | "unsupported-browser"
  | "need-chrome"
  | "need-safari"
  | "need-install";

export interface SetupView {
  key: NotificationKey | InstallSetup["state"];
  tone: SetupTone;
  badge: string;
  // 배너 전용. 카드는 `heading` 을 직접 받는다.
  title: string;
  body: string;
  guide?: string;
  action?: SetupAction;
}

interface Copy {
  tone: SetupTone;
  title: string;
  body: string;
  badge?: string;
  guide?: (env: Environment) => string;
}

const toView = (key: SetupView["key"], copy: Copy, env: Environment, action?: SetupAction) => ({
  key,
  tone: copy.tone,
  badge: copy.badge ?? TONE_BADGE[copy.tone],
  title: copy.title,
  body: copy.body,
  guide: copy.guide?.(env),
  action,
});

const TONE_BADGE: Record<SetupTone, string> = {
  on: "받는 중",
  off: "꺼짐",
  blocked: "받을 수 없음",
};

const KEY_BY_REASON = {
  "insecure-context": "insecure",
  "no-push-service": "no-push-service",
  "unsupported-browser": "unsupported-browser",
} as const satisfies Record<string, NotificationKey>;

const KEY_BY_BLOCKER = {
  browser: (b: Extract<NotificationBlocker, { kind: "browser" }>): NotificationKey =>
    b.target === "chrome" ? "need-chrome" : "need-safari",
  install: (): NotificationKey => "need-install",
};

const KEY_BY_STATE: Record<NotificationSetup["state"], (n: NotificationSetup) => NotificationKey> =
  {
    on: () => "on",
    denied: () => "denied",
    unavailable: (n) => KEY_BY_REASON[n.reason ?? "unsupported-browser"],
    off: (n) =>
      n.blockedBy
        ? KEY_BY_BLOCKER[n.blockedBy.kind](n.blockedBy as Extract<NotificationBlocker, never>)
        : "ready",
  };

const NOTIFICATION_COPY: Record<NotificationKey, Copy> = {
  on: {
    tone: "on",
    title: "알림 받는 중",
    body: "위험 상황이 생기면 이 기기로 알림이 옵니다.",
  },
  ready: {
    tone: "off",
    title: "알림 받기",
    body: "안전 이벤트가 발생하면 즉시 알려드립니다.",
  },
  denied: {
    tone: "blocked",
    title: "알림이 차단되어 있습니다",
    body: "차단은 사이트가 풀 수 없어 직접 바꿔주셔야 합니다.",
    guide: (env) => notificationGuideText(env.os, env.browser),
  },
  insecure: {
    tone: "blocked",
    title: "이 주소에서는 알림을 받을 수 없습니다",
    body: "알림은 https 주소 또는 localhost 에서만 동작합니다.",
  },
  "no-push-service": {
    tone: "blocked",
    title: "이 기기에서는 알림을 받을 수 없습니다",
    body: "권한은 허용됐지만 푸시가 오지 않습니다. 구글 플레이 서비스가 없는 기기에서 발생합니다.",
  },
  "unsupported-browser": {
    tone: "blocked",
    title: "이 브라우저에서는 알림을 받을 수 없습니다",
    body: "Chrome(안드로이드·데스크톱) 또는 Safari(iPhone)에서 열어주세요.",
  },
  /*
    `body` 는 그것만 읽어도 무엇을 하면 되는지 알아야 한다 — 카드(`SetupCard`)는 `title` 을
    그리지 않는다. 처방을 title 에만 두면 카드가 "안 된다"까지만 말하고 끝난다.
  */
  "need-chrome": {
    tone: "off",
    title: "Chrome에서 열어주세요",
    body: "지금 브라우저에서는 알림을 받을 수 없습니다. Chrome 또는 Edge에서 열어주세요.",
  },
  "need-safari": {
    tone: "off",
    title: "Safari에서 열어주세요",
    body: "iPhone은 Safari에서만 알림을 받을 수 있습니다.",
  },
  "need-install": {
    tone: "off",
    title: "홈 화면에 추가",
    body: "iPhone은 홈 화면에 추가해야 알림을 받을 수 있습니다.",
    guide: (env) => installGuideText(env.os),
  },
};

const NOTIFICATION_ACTION: Partial<
  Record<NotificationKey, (n: NotificationSetup) => SetupAction | undefined>
> = {
  ready: (n) => n.request && { label: "알림 켜기", onClick: () => void n.request?.() },
  "need-chrome": (n) =>
    n.blockedBy?.kind === "browser" && n.blockedBy.openInChrome
      ? { label: "Chrome으로 열기", onClick: n.blockedBy.openInChrome }
      : undefined,
  "need-install": (n) => {
    const b = n.blockedBy;
    return b?.kind === "install" && b.promptInstall
      ? { label: "설치", onClick: () => void b.promptInstall?.() }
      : undefined;
  },
};

export function notificationView(n: NotificationSetup, env: Environment): SetupView {
  const key = KEY_BY_STATE[n.state](n);
  return toView(key, NOTIFICATION_COPY[key], env, NOTIFICATION_ACTION[key]?.(n));
}

const INSTALL_TITLE = "앱 설치";
const NOT_INSTALLED_BODY =
  "홈 화면에서 바로 열리고, 알림을 눌렀을 때 브라우저 탭이 아니라 앱으로 열립니다.";
const installGuide = (env: Environment) => installGuideText(env.os);

const INSTALL_COPY: Record<InstallSetup["state"], Copy> = {
  installed: {
    tone: "on",
    badge: "설치됨",
    title: INSTALL_TITLE,
    body: "홈 화면 아이콘으로 바로 열 수 있고, 알림도 앱 이름으로 도착합니다.",
  },
  available: {
    tone: "off",
    badge: "미설치",
    title: INSTALL_TITLE,
    body: NOT_INSTALLED_BODY,
    guide: installGuide,
  },
  manual: {
    tone: "off",
    badge: "미설치",
    title: INSTALL_TITLE,
    body: NOT_INSTALLED_BODY,
    guide: installGuide,
  },
  unsupported: {
    tone: "blocked",
    badge: "불가",
    title: INSTALL_TITLE,
    body: "이 브라우저에서는 앱으로 설치할 수 없습니다.",
  },
};

export function installView(i: InstallSetup, env: Environment): SetupView {
  const action = i.promptInstall && {
    label: "설치",
    onClick: () => void i.promptInstall?.(),
  };
  return toView(i.state, INSTALL_COPY[i.state], env, action || undefined);
}
